// MapView (§24.2, §12.5, WP6): the environment of one round. A per-map arena builder makes the
// arena, its hazards and its telegraphs; this module adds what every map shares — the gradient sky
// with clouds and confetti, the void surface with KO splash rings, the toy audience on floating
// shelves, map debris — merges static props per material, and fits the key light's shadow frustum
// to the arena. Hazards are drawn from `view.hazard` and `view.map`, never re-simulated.
import {
  Group, Matrix4, Mesh, Vector3, type Camera, type DirectionalLight, type Material, type Object3D, type OrthographicCamera, type WebGLRenderer,
} from 'three';
import { EV, type SimEvent } from '@shared/events';
import type { MapDef } from '@shared/maps/mapTypes';
import { MapId } from '@shared/ids';
import type { RoundCard } from '@shared/protocol/messages';
import type { FrameView } from '../../game/FrameView';
import type { MaterialLibrary } from '../materials/library';
import type { QualityTier } from '../Renderer';
import { buildArena } from './arenas/index';
import { B, type Arena, type ArenaKit } from './arenas/types';
import { measureBudget } from './budget';
import { mapDebug, type MapViewDebugHandle } from './debug/state';
import { createAudience } from './env/audience';
import { createConfetti, createSkyDome } from './env/sky';
import { createSplashRings } from './env/splash';
import { createVoidSurface } from './env/void';
import { Batch, hullGeometry } from './kit/geometry';
import { chromeMaterial, skyEnvTexture } from './kit/chrome';
import { createEnvUniforms, glowMaterial, outlineMaterial, sharedOutlineScale, surfaceMaterial } from './kit/materials';
import { createMapParticles } from './kit/particles';
import { MAP_PALETTES } from './palettes';
import { createMapProbe, defaultModeFor } from './probe';
import { createSolidsOverlay, type SolidsOverlay } from './SolidsOverlay';

export interface MapView {
  readonly root: Object3D;
  readonly palette: MapPalette;        // used by post grade, VFX tints, UI accents
  readonly bounds: { minX: number; maxX: number; minY: number; maxY: number };
  readonly voidKind: MapDef['voidKind'];
  update(view: FrameView): void;       // hazard poses from view.hazard; tiles/wedges/tilt from view.map; audience bounce from view.excitement
  onEvent(ev: SimEvent, view: FrameView): void;   // tile/wedge/bumper/flipper flashes
  dispose(): void;
  /**
   * (Additive, WP6.) Visual ground offset in meters at sim (x, y): the Teacup saucer tilts visually
   * while gameplay stays planar (§6.2), so rigs, blob shadows and VFX may add this to their z.
   */
  surfaceOffset?(x: number, y: number): number;
  /** (Additive, WP6.) The Lobby's three vote-card pads show these round-1 cards (§6.1). */
  setVoteCards?(cards: readonly RoundCard[]): void;
}
export interface MapPalette { base: string; accent1: string; accent2: string; void: string; skyTop: string; skyBottom: string; fog: string; light: string }

/** §12.3 key light direction (toward the light). */
export const KEY_LIGHT_DIR = new Vector3(-0.5, 1, 0.6).normalize();
/** §12.3: the shadow frustum is the map bounds plus 2 m. */
const SHADOW_PAD = 2;

const SEGMENT_SCALE: Readonly<Record<QualityTier, number>> = { high: 1, medium: 0.75, low: 0.5, potato: 0.4 };

export interface ShadowBox { minX: number; maxX: number; minZ: number; maxZ: number; minY: number; maxY: number }

/**
 * Fits a directional light's orthographic shadow camera to the light-space extent of a box in
 * three.js space (sim bounds on x/z, heights on y), so no shadow-map texels are spent outside it.
 */
export function fitShadowFrustum(light: DirectionalLight, box: ShadowBox): void {
  const cam = light.shadow.camera as OrthographicCamera;
  light.updateMatrixWorld();
  light.target.updateMatrixWorld();
  const eye = new Vector3().setFromMatrixPosition(light.matrixWorld);
  const target = new Vector3().setFromMatrixPosition(light.target.matrixWorld);
  const toLight = new Matrix4().lookAt(eye, target, cam.up).setPosition(eye).invert();
  const p = new Vector3();
  let x0 = Infinity; let x1 = -Infinity; let y0 = Infinity; let y1 = -Infinity; let z0 = Infinity; let z1 = -Infinity;
  for (const x of [box.minX, box.maxX]) for (const y of [box.minY, box.maxY]) for (const z of [box.minZ, box.maxZ]) {
    p.set(x, y, z).applyMatrix4(toLight);
    x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x);
    y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
    z0 = Math.min(z0, p.z); z1 = Math.max(z1, p.z);
  }
  cam.left = x0;
  cam.right = x1;
  cam.bottom = y0;
  cam.top = y1;
  // Orthographic: a negative near is fine, so casters behind a close light are never clipped.
  cam.near = -z1 - 1;
  cam.far = -z0 + 1;
  cam.updateProjectionMatrix();
}

/** The map's environment, built for `quality` (detail levels follow §12.9). */
export function createMapView(mapId: MapId, seed: number, lib: MaterialLibrary, quality: QualityTier): MapView {
  return buildMapView(mapId, seed, lib, quality).view;
}

/** What `createMapView` builds, with the arena and shared layers exposed for tests and the debug page. */
export interface BuiltMapView {
  view: MapView;
  arena: Arena;
  splash: ReturnType<typeof createSplashRings>;
  audience: ReturnType<typeof createAudience>;
  handle: MapViewDebugHandle;
}

export function buildMapView(mapId: MapId, seed: number, lib: MaterialLibrary, quality: QualityTier): BuiltMapView {
  const palette = MAP_PALETTES[mapId];
  const probe = createMapProbe(mapId, defaultModeFor(mapId), seed);
  const def = probe.def;
  const root = new Group();
  root.name = `map:${def.name}`;
  const owned: { dispose(): void }[] = [];
  const env = createEnvUniforms(palette.skyBottom);
  const outlineScale = sharedOutlineScale(lib.outlineMaterial(0.035));
  const rich = quality === 'high' || quality === 'medium';
  const envMap = skyEnvTexture({ top: palette.skyTop, bottom: palette.skyBottom, ground: palette.void, accent: mapId === MapId.PINBALL ? palette.accent1 : undefined }, KEY_LIGHT_DIR);
  owned.push(envMap);

  const mats = {
    toon: surfaceMaterial(env, lib.rampTexture, { key: 'props', vertexColors: true, rim: 0.3, gloss: 0.12 }),
    gloss: surfaceMaterial(env, lib.rampTexture, { key: 'props-gloss', vertexColors: true, rim: 0.4, gloss: 0.6, glossEdge: 0.92 }),
    glow: glowMaterial({ vertexColors: true }),
    hull: outlineMaterial(0.035, outlineScale),
    hullBold: outlineMaterial(0.055, outlineScale),
    bg: surfaceMaterial(env, lib.rampTexture, { key: 'props-bg', vertexColors: true, rim: 0.3, gloss: 0.25 }),
    bgGlow: glowMaterial({ vertexColors: true }),
    // Potato (SwiftShader) skips PBR and the PMREM bake: polished metal is a glossy toon steel there.
    chrome: quality === 'potato'
      ? surfaceMaterial(env, lib.rampTexture, { key: 'props-chrome-lite', vertexColors: true, rim: 0.55, gloss: 0.9, glossEdge: 0.9 })
      : chromeMaterial(envMap),
  } satisfies ArenaKit['mats'];
  owned.push(...Object.values(mats));
  const debrisMat = surfaceMaterial(env, lib.rampTexture, { key: 'debris', rim: 0.3, gloss: 0.3 });
  owned.push(debrisMat);
  const particles = createMapParticles(quality, debrisMat);
  owned.push(particles);

  const batch = new Batch();
  const kit: ArenaKit = {
    mapId, seed, tier: quality, lib, palette, env, ramp: lib.rampTexture, outlineScale, particles, probe,
    sunDir: KEY_LIGHT_DIR, batch, mats, rich, envMap,
    seg: (hi, lo = 6) => Math.max(lo, Math.round(hi * SEGMENT_SCALE[quality])),
  };
  const arena: Arena = buildArena(kit);
  root.add(arena.root);
  const layout = arena.layout;

  const sky = createSkyDome(layout.sky, env, KEY_LIGHT_DIR);
  const voidSurface = createVoidSurface(def.voidKind, layout.voidColors, quality, env, KEY_LIGHT_DIR,
    {
      centerX: def.center.x, centerY: def.center.y, edgeRadius: layout.voidEdge,
      shadow: layout.voidShadow ?? { x: def.center.x, y: def.center.y, hx: 0, hy: 0, r: layout.voidEdge, strength: 0.8 },
      pour: layout.voidPour,
      sky: { top: layout.sky.top, bottom: layout.sky.bottom },
    });
  const splash = createSplashRings(def.voidKind, layout.splash, env);
  const confetti = createConfetti(layout.confetti.colors, quality, seed, env, layout.confetti);
  const audience = createAudience(layout.audience, quality, seed, env, lib.rampTexture, outlineScale);
  owned.push(sky, voidSurface, splash, confetti, audience);
  audience.addShelves(batch, B.bg, rich ? B.bgHull : null);
  const envLayer = new Group();
  envLayer.name = 'env';
  envLayer.add(sky.mesh, voidSurface.mesh, splash.mesh, confetti.mesh, audience.root);
  root.add(envLayer, particles.mesh);
  const bgMeshes = finishBatch(kit, batch, root);
  for (const m of bgMeshes) if (m.name.startsWith('batch:bg') || m.name === 'batch:chrome') envLayer.add(m);

  // ---- per-frame state ---------------------------------------------------------------------
  let time = 0;
  let light: DirectionalLight | null = null;
  /** The light pose and frustum of the last fit: refit when any of them changed (the light moved, or someone else fitted it). */
  const fitKey = new Float64Array(12).fill(NaN);
  const fitNow = new Float64Array(12);
  const b = def.bounds;
  const shadowBox: ShadowBox = {
    minX: b.minX - SHADOW_PAD, maxX: b.maxX + SHADOW_PAD, minZ: b.minY - SHADOW_PAD, maxZ: b.maxY + SHADOW_PAD,
    minY: layout.shadowY[0], maxY: layout.shadowY[1],
  };
  let overlay: SolidsOverlay | null = null;
  let lastCamera: Camera | null = null;
  let lastRenderer: WebGLRenderer | null = null;
  sky.mesh.onBeforeRender = (renderer, _scene, camera) => {
    lastCamera = camera;
    lastRenderer = renderer;
  };
  const frame = { view: null as unknown as FrameView, time: 0, dt: 0, beat: 0 };

  /** Finds the scene's key light once and refits its shadow frustum whenever the light moves. */
  function syncLight(): void {
    if (!light) {
      const parent = root.parent;
      if (!parent) return;
      for (const c of parent.children) {
        if ((c as DirectionalLight).isDirectionalLight) {
          light = c as DirectionalLight;
          break;
        }
      }
      if (!light) return;
    }
    const cam = light.shadow.camera;
    const lp = light.position;
    const tp = light.target.position;
    fitNow.set([lp.x, lp.y, lp.z, tp.x, tp.y, tp.z, cam.left, cam.right, cam.top, cam.bottom, cam.near, cam.far]);
    let same = true;
    for (let i = 0; i < 12; i++) if (!(Math.abs(fitNow[i] - fitKey[i]) < 1e-6)) same = false;
    if (same) return;
    fitShadowFrustum(light, shadowBox);
    fitKey.set([lp.x, lp.y, lp.z, tp.x, tp.y, tp.z, cam.left, cam.right, cam.top, cam.bottom, cam.near, cam.far]);
  }

  function syncDebug(v: FrameView): void {
    if (mapDebug.overlay && !overlay) {
      overlay = createSolidsOverlay();
      overlay.setRound(probe);
      overlay.setRendered(() => arena.debugHazards?.() ?? []);
      root.add(overlay.root);
    }
    if (overlay) {
      overlay.root.visible = mapDebug.overlay;
      overlay.setSource(mapDebug.solids);
      if (mapDebug.overlay) overlay.update(v);
    }
    envLayer.visible = !mapDebug.isolate;
  }

  const handle: MapViewDebugHandle = {
    mapId, tier: quality,
    get camera() { return lastCamera; },
    get renderer() { return lastRenderer; },
    hazards: () => arena.debugHazards?.() ?? [],
    budget: () => measureBudget(root, light?.castShadow === true),
    flatHazards: (on) => flatHazards(arena.root, on),
  };

  const mapView: MapView = {
    root, palette, bounds: def.bounds, voidKind: def.voidKind,
    update(v) {
      const dt = Math.min(0.1, Math.max(0, v.dt)) * (v.timeScale > 0 ? v.timeScale : 1);
      time += dt;
      env.uTime.value = time;
      env.uBeat.value = v.clock.beat;
      env.uExcite.value = v.excitement;
      probe.sync(v);
      frame.view = v;
      frame.time = time;
      frame.dt = dt;
      frame.beat = v.clock.beat;
      arena.update(frame);
      particles.update(dt);
      syncLight();
      syncDebug(v);
      mapDebug.active = handle;
    },
    onEvent(ev, v) {
      frame.view = v;
      frame.time = time;
      frame.beat = v.clock.beat;
      if (ev.type === EV.KO) {
        splash.spawn(ev.x, ev.y, time);
        voidSurface.ripple(ev.x, ev.y, 1, time);
        audience.wave(ev.x, ev.y, time);
      }
      arena.onEvent(ev, frame);
    },
    dispose() {
      arena.dispose();
      overlay?.dispose();
      for (const o of owned) o.dispose();
      root.traverse((o) => {
        if ((o as Mesh).isMesh && o.userData.wp6Owned) (o as Mesh).geometry.dispose();
      });
      if (mapDebug.active === handle) mapDebug.active = null;
    },
  };
  if (arena.surfaceOffset) mapView.surfaceOffset = arena.surfaceOffset.bind(arena);
  const withCards = arena as Arena & { setVoteCards?: MapView['setVoteCards'] };
  if (withCards.setVoteCards) mapView.setVoteCards = withCards.setVoteCards.bind(arena);
  return { view: mapView, arena, splash, audience, handle };
}

/**
 * Merges a batch into meshes on `parent`: one mesh per bucket, plus each outline bucket as one
 * welded hull. Arena groups that move as a unit (the tilting saucer) call this with their own batch.
 */
export function finishBatch(kit: ArenaKit, batch: Batch, parent: Object3D, opts: { castShadow?: boolean; receiveShadow?: boolean } = {}): Mesh[] {
  const out: Mesh[] = [];
  const add = (bucket: string, mat: Material, cast: boolean, receive: boolean): void => {
    const g = batch.take(bucket);
    if (!g) return;
    const m = new Mesh(g, mat);
    m.name = `batch:${bucket}`;
    m.castShadow = cast && (opts.castShadow ?? true);
    m.receiveShadow = receive && (opts.receiveShadow ?? true);
    m.userData.wp6Owned = true;
    parent.add(m);
    out.push(m);
  };
  const addHull = (bucket: string, mat: Material): void => {
    const g = batch.take(bucket);
    if (!g) return;
    const hull = hullGeometry(g);
    g.dispose();
    const m = new Mesh(hull, mat);
    m.name = `batch:${bucket}`;
    m.userData.wp6Owned = true;
    parent.add(m);
    out.push(m);
  };
  add(B.toon, kit.mats.toon, true, true);
  add(B.gloss, kit.mats.gloss, true, true);
  add(B.glow, kit.mats.glow, false, false);
  add(B.bg, kit.mats.bg, false, false);
  add(B.bgGlow, kit.mats.bgGlow, false, false);
  add(B.chrome, kit.mats.chrome, false, false);
  addHull(B.hull, kit.mats.hull);
  addHull(B.hullBold, kit.mats.hullBold);
  addHull(B.bgHull, kit.mats.hull);
  return out;
}

/** Swaps every hazard mesh (userData.hazard) to a flat key material for pixel checks. */
const FLAT_SAVED = new Map<Mesh, Material | Material[]>();
let flatMat: Material | null = null;
function flatHazards(root: Object3D, on: boolean): void {
  if (on) {
    if (!flatMat) {
      const m = glowMaterial({ vertexColors: false, color: '#FF00FF' });
      m.fog = false;
      flatMat = m;
    }
    root.traverse((o) => {
      const m = o as Mesh;
      if (o.userData.hazardDecor) o.visible = false;
      if (!m.isMesh || !o.userData.hazard || FLAT_SAVED.has(m)) return;
      FLAT_SAVED.set(m, m.material);
      m.material = flatMat!;
    });
  } else {
    for (const [m, mat] of FLAT_SAVED) m.material = mat;
    FLAT_SAVED.clear();
    root.traverse((o) => {
      if (o.userData.hazardDecor) o.visible = true;
    });
  }
}
